import React, { Component } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Container,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import {
  getAppVersionSettings,
  updateAppVersionSettings,
} from "../../Endpoint/Admin/index";

class AppVersionSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      platform: "android",
      latestVersion: "",
      minVersion: "",
      updateUrl: "",
      updateMessage: "",
      forceUpdate: false,
      currentLiveVersion: "",
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "App Version Settings", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadSettings();
  }

  loadSettings = async () => {
    const { platform } = this.state;
    this.setState({ isLoading: true });

    const result = await getAppVersionSettings(platform);
    if (result && result.data) {
      const d = result.data;
      this.setState({
        latestVersion: d.latestVersion || "",
        minVersion: d.minVersion || "",
        updateUrl: d.updateUrl || "",
        updateMessage: d.updateMessage || "",
        forceUpdate: d.mustUpdate || false,
        currentLiveVersion: d.latestVersion || "",
        isLoading: false,
      });
    } else {
      this.setState({ isLoading: false });
    }
  };

  handleSave = async () => {
    const {
      platform,
      latestVersion,
      minVersion,
      updateUrl,
      updateMessage,
      forceUpdate,
    } = this.state;

    if (!latestVersion || !minVersion) {
      toast.error("Latest version and minimum version are required");
      return;
    }

    const result = await updateAppVersionSettings({
      platform,
      latestVersion,
      minVersion,
      updateUrl,
      updateMessage,
      forceUpdate,
    });

    if (result && result.success) {
      toast.success("App version settings updated!");
      this.setState({ currentLiveVersion: latestVersion });
    } else {
      toast.error(result?.message || "Failed to update settings");
    }
  };

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({ [name]: type === "checkbox" ? checked : value });
  };

  switchPlatform = (platform) => {
    this.setState({ platform }, this.loadSettings);
  };

  render() {
    const {
      isLoading,
      platform,
      latestVersion,
      minVersion,
      updateUrl,
      updateMessage,
      forceUpdate,
      currentLiveVersion,
      breadcrumbItems,
    } = this.state;

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs
              title="App Version Settings"
              breadcrumbItems={breadcrumbItems}
            />
            <ToastContainer />

            <Row>
              <Col lg={8}>
                <Card>
                  <CardBody>
                    <div className="d-flex align-items-center mb-4">
                      <h4 className="card-title mb-0 mr-3">
                        <i className="ri-smartphone-line mr-2"></i>
                        App Update Control
                      </h4>
                      <div className="ml-auto">
                        <Button
                          color={platform === "android" ? "success" : "light"}
                          size="sm"
                          className="mr-2"
                          onClick={() => this.switchPlatform("android")}
                        >
                          <i className="ri-android-line mr-1"></i> Android
                        </Button>
                        <Button
                          color={platform === "ios" ? "primary" : "light"}
                          size="sm"
                          onClick={() => this.switchPlatform("ios")}
                        >
                          <i className="ri-apple-line mr-1"></i> iOS
                        </Button>
                      </div>
                    </div>

                    {isLoading ? (
                      <div className="text-center py-4">
                        <i className="ri-loader-4-line ri-2x spin"></i>
                        <p className="mt-2 text-muted">Loading settings...</p>
                      </div>
                    ) : (
                      <>
                        {currentLiveVersion && (
                          <Alert color="info" className="mb-4">
                            <i className="ri-information-line mr-1"></i>
                            Current live version:{" "}
                            <strong>{currentLiveVersion}</strong> ({platform})
                          </Alert>
                        )}

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label>
                                Latest Version{" "}
                                <span className="text-danger">*</span>
                              </Label>
                              <Input
                                type="text"
                                name="latestVersion"
                                value={latestVersion}
                                onChange={this.handleChange}
                                placeholder="e.g. 3.0.5"
                              />
                              <small className="text-muted">
                                Users below this will see "Update Available"
                              </small>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>
                                Minimum Required Version{" "}
                                <span className="text-danger">*</span>
                              </Label>
                              <Input
                                type="text"
                                name="minVersion"
                                value={minVersion}
                                onChange={this.handleChange}
                                placeholder="e.g. 3.0.0"
                              />
                              <small className="text-muted">
                                Users below this MUST update (can't skip)
                              </small>
                            </FormGroup>
                          </Col>
                        </Row>

                        <FormGroup>
                          <Label>Update URL</Label>
                          <Input
                            type="text"
                            name="updateUrl"
                            value={updateUrl}
                            onChange={this.handleChange}
                            placeholder="Play Store or App Store URL"
                          />
                        </FormGroup>

                        <FormGroup>
                          <Label>Update Message</Label>
                          <Input
                            type="textarea"
                            name="updateMessage"
                            value={updateMessage}
                            onChange={this.handleChange}
                            rows={3}
                            placeholder="Message shown to users when update is available"
                          />
                        </FormGroup>

                        <FormGroup check className="mb-4">
                          <Label check>
                            <Input
                              type="checkbox"
                              name="forceUpdate"
                              checked={forceUpdate}
                              onChange={this.handleChange}
                            />{" "}
                            <strong className="text-danger">
                              Force Update
                            </strong>{" "}
                            - All users MUST update (cannot use app until
                            updated)
                          </Label>
                        </FormGroup>

                        <Button
                          color="primary"
                          onClick={this.handleSave}
                          className="px-4"
                        >
                          <i className="ri-save-line mr-1"></i> Save Settings
                        </Button>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col lg={4}>
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      <i className="ri-question-line mr-1"></i> How it works
                    </h5>
                    <ul className="list-unstyled">
                      <li className="mb-3">
                        <strong className="text-success">
                          <i className="ri-checkbox-circle-line mr-1"></i>
                          Latest Version
                        </strong>
                        <br />
                        <small className="text-muted">
                          Users below this see a popup with "Update" and "Later"
                          buttons. They can skip.
                        </small>
                      </li>
                      <li className="mb-3">
                        <strong className="text-warning">
                          <i className="ri-error-warning-line mr-1"></i>
                          Minimum Version
                        </strong>
                        <br />
                        <small className="text-muted">
                          Users below this see a popup that CANNOT be dismissed.
                          They must update to use the app.
                        </small>
                      </li>
                      <li className="mb-3">
                        <strong className="text-danger">
                          <i className="ri-alarm-warning-line mr-1"></i>
                          Force Update
                        </strong>
                        <br />
                        <small className="text-muted">
                          When enabled, ALL users regardless of version must
                          update. Use for critical security fixes.
                        </small>
                      </li>
                    </ul>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

export default AppVersionSettings;
